import React, { useEffect, useState } from "react";
import { Mail, MessageSquare, Phone, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, STAGES, timeAgo } from "@/lib/api";
import { ChatPanel } from "@/components/ChatPanel";
import { AnswersView } from "@/components/QuoteForm";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DeliveryBadge } from "@/components/DeliveryBadge";

const TEMPLATES = [
  { key: "thank_you", label: "Thanks for visiting" },
  { key: "quote_reminder", label: "Quote reminder" },
  { key: "booking_invite", label: "Booking invite" },
];

function FollowUp({ lead, onDone }: { lead: any; onDone: () => void }) {
  const [template, setTemplate] = useState("thank_you");
  const send = async (channel: string) => {
    try {
      const { data } = await api.post(`/vendor/leads/${lead.id}/followup`, { channel, template });
      toast.success(data.status === "mocked" ? `${channel.toUpperCase()} logged (Twilio mocked)` : data.status === "sent" ? "Email sent" : "Email failed to send");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!lead.marketing_consent)
    return <p className="rounded-xl border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-800" data-testid="followup-no-consent">This lead didn't opt in to follow-up messages. Use in-app chat instead.</p>;
  return (
    <div className="space-y-3" data-testid="followup-panel">
      <Select value={template} onValueChange={setTemplate}>
        <SelectTrigger data-testid="followup-template-select"><SelectValue /></SelectTrigger>
        <SelectContent>{TEMPLATES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      <div className="grid grid-cols-3 gap-2">
        <Button variant="secondary" onClick={() => send("email")} data-testid="followup-email-button"><Mail className="mr-1 h-4 w-4" /> Email</Button>
        <Button variant="secondary" onClick={() => send("sms")} disabled={!lead.phone} data-testid="followup-sms-button"><Smartphone className="mr-1 h-4 w-4" /> SMS</Button>
        <Button variant="secondary" onClick={() => send("whatsapp")} disabled={!lead.phone} data-testid="followup-whatsapp-button"><MessageSquare className="mr-1 h-4 w-4" /> WhatsApp</Button>
      </div>
      <p className="text-xs text-muted-foreground">Templated messages link back to the customer's vendor list. SMS/WhatsApp run in mock mode until Twilio keys are added.</p>
    </div>
  );
}

export function LeadDrawer({ leadId, onClose, onChanged }: { leadId: string; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<any>(null);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");

  const load = () => api.get(`/vendor/leads/${leadId}`).then((r) => { setD(r.data); setTags(r.data.lead.tags.join(", ")); });
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const patch = async (body: any) => {
    try {
      await api.patch(`/vendor/leads/${leadId}`, body);
      await load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/vendor/leads/${leadId}/notes`, { body: note });
    setNote("");
    load();
  };
  const refresh = () => { load(); onChanged(); };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="lead-drawer">
        {!d ? <><SheetTitle className="sr-only">Lead details</SheetTitle><p className="text-muted-foreground">Loading...</p></> : (
          <>
            <SheetHeader>
              <SheetTitle className="text-2xl font-semibold tracking-tight" data-testid="lead-drawer-name">{d.lead.name}</SheetTitle>
            </SheetHeader>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {d.lead.email}</p>
              {d.lead.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {d.lead.phone}</p>}
              <p className="font-mono text-xs">{d.lead.event_name} · {d.lead.interaction_count} interaction(s){d.other_events.length ? ` · also at ${d.other_events.map((o: any) => o.event_name).join(", ")}` : ""}</p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <Select value={d.lead.stage} onValueChange={(v) => patch({ stage: v })}>
                <SelectTrigger data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input defaultValue={d.lead.partner_name} placeholder="Partner / household" onBlur={(e) => e.target.value !== d.lead.partner_name && patch({ partner_name: e.target.value })} data-testid="lead-partner-input" />
              <Input className="col-span-2" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma separated"
                onBlur={() => patch({ tags: tags.split(",") })} data-testid="lead-tags-input" />
            </div>
            <Tabs defaultValue="activity" className="mt-6">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="activity" data-testid="lead-tab-activity">Activity</TabsTrigger>
                <TabsTrigger value="quote" data-testid="lead-tab-quote">Answers</TabsTrigger>
                <TabsTrigger value="chat" data-testid="lead-tab-chat">Chat</TabsTrigger>
                <TabsTrigger value="follow" data-testid="lead-tab-followup">Follow</TabsTrigger>
                <TabsTrigger value="consent" data-testid="lead-tab-consent">Consent</TabsTrigger>
              </TabsList>
              <TabsContent value="activity" className="space-y-4 pt-3">
                <div className="space-y-2">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for the team..." data-testid="lead-note-input" />
                  <Button size="sm" onClick={addNote} className="bg-primary text-white hover:bg-primary/90" data-testid="lead-note-submit">Add note</Button>
                </div>
                <ol className="relative space-y-4 border-l border-border pl-4" data-testid="lead-timeline">
                  {d.interactions.map((i: any) => (
                    <li key={i.id} className="text-sm">
                      <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-emerald-500" />
                      <p className="font-mono text-[10px] uppercase tracking-wider text-emerald-600">{i.type.replace("_", " ")}</p>
                      <p>{i.detail}</p>
                      {i.delivery_status && <DeliveryBadge i={i} />}
                      <p className="text-xs text-muted-foreground">{i.staff_name ? `${i.staff_name} · ` : ""}{timeAgo(i.created_at)}</p>
                    </li>
                  ))}
                </ol>
              </TabsContent>
              <TabsContent value="quote" className="space-y-5 pt-3">
                {d.base && <div data-testid="lead-base-answers"><AnswersView answers={d.base} testid="lead-base-answers-list" /></div>}
                <div data-testid="lead-extra-answers">
                  {d.base && <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your questions</p>}
                  <AnswersView answers={d.answers} empty={d.base ? "No answers to your extra questions yet." : undefined} />
                </div>
              </TabsContent>
              <TabsContent value="chat" className="pt-3"><ChatPanel url={`/vendor/leads/${leadId}/messages`} me="vendor" /></TabsContent>
              <TabsContent value="follow" className="pt-3"><FollowUp lead={d.lead} onDone={refresh} /></TabsContent>
              <TabsContent value="consent" className="space-y-3 pt-3" data-testid="lead-consent-panel">
                {d.consents.map((c: any) => (
                  <div key={c.id} className="rounded-xl border border-border p-3 text-xs">
                    <p className="flex items-center gap-1.5 font-semibold text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> {c.consent_text_version} · {new Date(c.created_at).toLocaleString()}</p>
                    <p className="mt-1 text-muted-foreground">Share: {c.share_text}</p>
                    <p className="mt-1 text-muted-foreground">Marketing: {c.marketing_consent ? c.marketing_text || "Opted in" : "Not given"}</p>
                    <p className="mt-1 font-mono text-[10px] text-slate-500">via {(c.source || "qr").toUpperCase()}</p>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
